import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import { QuizImage } from "./quiz-image.js";
import { DesmosGraph } from "./desmos-graph.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { QUIZ_EXPLANATION_STYLES } from "./quiz-styles.js";
import { useQuizStyleVariant } from "./quiz-style-context.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
import Lightbulb from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/lightbulb.js";
const SHELL_REVEAL_DURATION = 0.22;
const CONTENT_FADE_DURATION = 0.18;
function QuizSolveDescription({
  description: descProp,
  commentaryImages,
  vars,
  id = "solve-desc",
  className,
  actionVisible,
  actionLabel,
  onActionClick
}) {
  const { t } = useTranslation("quiz");
  const variant = useQuizStyleVariant();
  const explanationStyles = QUIZ_EXPLANATION_STYLES[variant];
  const description = descProp ?? {
    text: void 0,
    image: null,
    imageMobile: null,
    desmos: null
  };
  const hasText = description.text && description.text.trim().length > 0;
  const hasImage = description.image || description.imageMobile;
  const hasDesmos = !!description.desmos;
  const imgs = commentaryImages ?? void 0;
  return /* @__PURE__ */ jsx(
    motion.div,
    {
      "data-testid": "quiz-solve-description",
      initial: { height: 0 },
      animate: { height: "auto" },
      transition: { duration: SHELL_REVEAL_DURATION, ease: "easeOut" },
      className: cn(
        "overflow-hidden",
        variant === "lc" ? "rounded-[10px] border-[1.5px] border-lc-success-100 bg-lc-success-50" : "bg-linear-to-r from-gray-50 rounded-2xl border border-gray-200 to-slate-50",
        className
      ),
      children: /* @__PURE__ */ jsx(
        motion.div,
        {
          "data-testid": "quiz-solve-description-content",
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: {
            delay: SHELL_REVEAL_DURATION,
            duration: CONTENT_FADE_DURATION,
            ease: "easeOut"
          },
          className: cn(
            variant === "lc" ? "px-[10px] py-2" : "flex items-start gap-3 p-4"
          ),
          children: variant === "lc" ? /* @__PURE__ */ jsxs("div", { className: "w-full space-y-3 text-[16px] font-normal leading-[1.6] text-lc-text-primary", children: [
            hasText ? formatText(description.text, id, imgs) : null,
            hasImage ? /* @__PURE__ */ jsx(
              QuizImage,
              {
                image: description.image ?? void 0,
                imageMobile: description.imageMobile ?? void 0,
                alt: t("shared.explanationImage"),
                className: "my-2"
              }
            ) : null,
            hasDesmos ? /* @__PURE__ */ jsx("div", { className: "my-2 flex justify-center", children: /* @__PURE__ */ jsx(
              DesmosGraph,
              {
                state: description.desmos,
                vars,
                className: "rounded-xl"
              }
            ) }) : null
          ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("div", { className: "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lc-mint-900", children: /* @__PURE__ */ jsx(Lightbulb, { className: "h-4 w-4 text-white" }) }),
            /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-3", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex h-8 items-center justify-between gap-2", children: [
                /* @__PURE__ */ jsx("p", { className: "text-lg font-semibold text-gray-700", children: t("shared.explanation") }),
                actionVisible && actionLabel && onActionClick ? /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: onActionClick,
                    className: explanationStyles.actionButton,
                    children: actionLabel
                  }
                ) : null
              ] }),
              hasText ? /* @__PURE__ */ jsx("div", { className: "text-xl leading-relaxed text-gray-600", children: formatText(description.text, id, imgs) }) : null,
              hasImage ? /* @__PURE__ */ jsx(
                QuizImage,
                {
                  image: description.image ?? void 0,
                  imageMobile: description.imageMobile ?? void 0,
                  alt: t("shared.explanationImage"),
                  className: "my-2"
                }
              ) : null,
              hasDesmos ? /* @__PURE__ */ jsx("div", { className: "my-2 flex justify-center", children: /* @__PURE__ */ jsx(
                DesmosGraph,
                {
                  state: description.desmos,
                  vars,
                  className: "rounded-xl"
                }
              ) }) : null
            ] })
          ] })
        }
      )
    }
  );
}
export {
  QuizSolveDescription
};
//# sourceMappingURL=quiz-solve-description.js.map
